import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createSearchParams, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { GradeTrackLeaveContext } from '../../contexts/GradeTrackLeaveContext';
import { useProgramSidebar } from '../../contexts/ProgramSidebarContext';
import { getSession, UserDisplayInfo } from '../../services/authService';
import { getAnnualProjectPrograms } from '../../services/annualProjectProgramsService';
import BrandLogo from '../BrandLogo';
import UserProfileAvatar from '../UserProfileAvatar';
import {
  listProfileBubbleCategories,
  listProfileBubbleMessages,
  PROFILE_BUBBLE_MESSAGES_CHANGED_EVENT,
} from '../../services/profileBubbleMessageService';
import { APP_DISPLAY_NAME } from '../../appMeta';
import { getTracks } from '../../services/gradeTrackService';
import { getGradeTrackProgramPreferences, getGradeTrackSections } from '../../services/sectionLayoutService';
import type { PageSectionLayout } from '../../services/sectionLayoutService';
import { filterTracksSubscribedToGradeTrackSections } from '../../utils/gradeTrackSectionSubscription';
import { Grade, Track } from '../../types/domain';
import { parseGradeDisplayName } from '../../utils/gradeDisplayName';
import { academicYearShortLabel, classYearStatus } from '../../utils/classAcademicYear';
import { loadTrackAliasOrderMap } from '../../utils/trackAliasOrderStorage';
import { getOrderedTrackNamesForGrade } from '../../utils/trackOrderForGrade';
import { TRACK_PROGRAM_SPLIT_CHANGE_EVENT } from '../../utils/trackProgramSplitStorage';
import { loadTrackProgramModeMap } from '../../utils/trackProgramModeStorage';
import { applyGradeTrackProgramPrefsToLocalStores } from '../../utils/gradeTrackProgramPrefsLocalSeed';
import { loadTrackProgramSyncRepMap } from '../../utils/trackProgramSyncRepStorage';
import {
  ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT,
  loadAnnualProjectPrograms,
} from '../../utils/annualProjectProgramsStorage';
import {
  ANNUAL_SWAP_TERM_ORDER_CHANGED_EVENT,
  loadAnnualSwapTermOrder,
  orderedAcademicTerms,
} from '../../utils/annualSwapTermOrderStorage';
import { getAcademicTerms } from '../../services/calendarService';
import { toISODate } from '../../utils/date';
import { usePresetMenuGradeStatus } from '../../hooks/usePresetMenuGradeStatus';
import {
  isProgramProgressFamilyLeaveDirty,
  isProgramProgressFamilyRoute,
} from '../../utils/programRoutes';
import {
  hasSidebarProgramExpandStored,
  loadSidebarProgramExpand,
  saveSidebarProgramExpand,
} from '../../utils/sidebarProgramExpandStorage';
import {
  pathRequiresSingleClassSelection,
} from '../../utils/sidebarClassView';
import { usePaidPassGate } from '../../contexts/PaidPassGateContext';
import { planFeatureForPath, type PlanFeatureKey } from '../../constants/planFeatures';
import { isPlatformAdminEmail } from '../../constants/platformAdmin';
import {
  fetchAmIPlatformManager,
  PLATFORM_MANAGER_ADMIN_PATHS,
} from '../../services/platformManagerService';
import {
  ADMIN_SIDEBAR_MENU_VISIBLE_CHANGED_EVENT,
  readAdminSidebarMenuVisiblePref,
} from '../../utils/adminSidebarMenuPref';
import {
  getBoundSubscriptionPassUserId,
  hasActiveBetaTesterPass,
  loadSubscriptionPassState,
  SUBSCRIPTION_PASS_CHANGED_EVENT,
} from '../../utils/subscriptionPassStorage';

function SidebarClassPillInner({ grade }: { grade: Grade }) {
  const parsed = parseGradeDisplayName(grade.name);
  return (
    <>
      {grade.academic_year ? (
        <span
          className={`cp-class-year-tag${classYearStatus(grade.academic_year) !== 'active' ? ' is-locked' : ''}`}
          title={classYearStatus(grade.academic_year) !== 'active' ? '기록 중지된 지난 학년도 클래스' : undefined}
        >
          {academicYearShortLabel(grade.academic_year)}
        </span>
      ) : null}
      <span className="cp-grade-track-class-block-label">{parsed.title}</span>
      <span className="cp-sidebar-class-chip-row" aria-hidden>
        {parsed.grades.map((num) => (
          <span key={num} className="class-grade-chip-display">
            {num}
          </span>
        ))}
      </span>
    </>
  );
}

type SidebarNoteTileIconKind =
  | 'admin'
  | 'dashboard'
  | 'beta'
  | 'daily'
  | 'todo'
  | 'assignment'
  | 'point'
  | 'budget'
  | 'curriculum'
  | 'settings'
  | 'plan';

const SIDEBAR_NOTE_TILE_ICON_PATHS: Record<
  Exclude<SidebarNoteTileIconKind, 'assignment' | 'point'>,
  React.ReactNode
> = {
  admin: (
    <>
      <path d="M12 3l7.5 3v5.5c0 4.6-3.2 8.2-7.5 9.5-4.3-1.3-7.5-4.9-7.5-9.5V6L12 3z" />
      <path d="M9 12l2.2 2.2L15.5 10" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3.5" y="3.5" width="7" height="8" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1.6" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1.6" />
      <rect x="3.5" y="14.5" width="7" height="6" rx="1.6" />
    </>
  ),
  beta: (
    <>
      <path d="M9 3h6" />
      <path d="M10 3v6.2L6.2 17.2A2.4 2.4 0 0 0 8.4 21h7.2a2.4 2.4 0 0 0 2.2-3.8L14 9.2V3" />
      <path d="M8.6 14h6.8" />
    </>
  ),
  daily: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </>
  ),
  todo: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <path d="M8 12.5l2.8 2.8L16.5 9.5" />
    </>
  ),
  budget: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="2.5" />
      <path d="M3 10h18M16 15h2" />
      <path d="M6 6l9-2.5 1 2.5" />
    </>
  ),
  curriculum: (
    <>
      <path d="M12 6.5C10.2 5 7.6 4.5 4 4.5v14c3.6 0 6.2.5 8 2 1.8-1.5 4.4-2 8-2v-14c-3.6 0-6.2.5-8 2z" />
      <path d="M12 6.5v14" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1" />
    </>
  ),
  plan: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
};

const SIDEBAR_NOTE_TILE_ASSET_ICONS: Partial<Record<SidebarNoteTileIconKind, string>> = {
  assignment: 'task2.svg',
  point: 'point.svg',
};

function SidebarNoteTileIcon({ kind }: { kind: SidebarNoteTileIconKind }) {
  const asset = SIDEBAR_NOTE_TILE_ASSET_ICONS[kind];
  if (asset) {
    return (
      <span
        className={`cp-sidebar-note-tile-icon cp-sidebar-note-tile-icon--asset cp-sidebar-note-tile-icon--${kind}`}
        style={
          {
            ['--cp-icon-mask' as string]: `url(${process.env.PUBLIC_URL || ''}/${asset})`,
          } as React.CSSProperties
        }
        aria-hidden="true"
      />
    );
  }
  return (
    <svg
      className={`cp-sidebar-note-tile-icon cp-sidebar-note-tile-icon--${kind}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {SIDEBAR_NOTE_TILE_ICON_PATHS[kind as Exclude<SidebarNoteTileIconKind, 'assignment' | 'point'>]}
    </svg>
  );
}

const PROFILE_BUBBLE_AUTO_HIDE_MS = 5000;

function pickRandomBubble(pool: string[], previous: string | null): string | null {
  if (pool.length === 0) return null;
  if (pool.length === 1) return pool[0];
  const candidates = pool.filter((s) => s !== previous);
  return candidates[Math.floor(Math.random() * candidates.length)] ?? pool[0];
}

/** 프로필 아바타 — 클릭 시 프로필 말풍선 관리 문구 중 하나를 랜덤 표시 */
function SidebarProfileAvatarBubble({
  userDisplay,
  onOpenClassSettings,
  onOpenClassMembersSettings,
}: {
  userDisplay: UserDisplayInfo;
  onOpenClassSettings?: () => void;
  onOpenClassMembersSettings?: () => void;
}) {
  const [pool, setPool] = useState<string[]>([]);
  const [bubble, setBubble] = useState<{ text: string; key: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const poolReadyRef = useRef(false);

  const refreshPool = useCallback(async (): Promise<string[] | null> => {
    try {
      const [categories, rows] = await Promise.all([
        listProfileBubbleCategories(),
        listProfileBubbleMessages(),
      ]);
      const shownIds = new Set(categories.filter((c) => c.is_shown).map((c) => c.id));
      const next = rows
        .filter((r) => shownIds.has(r.category_id))
        .map((r) => r.body)
        .filter((s) => s.trim());
      poolReadyRef.current = true;
      setPool(next);
      return next;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    void refreshPool();
    const onChanged = () => void refreshPool();
    window.addEventListener(PROFILE_BUBBLE_MESSAGES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(PROFILE_BUBBLE_MESSAGES_CHANGED_EVENT, onChanged);
  }, [refreshPool, userDisplay.userId]);

  useEffect(() => {
    if (!bubble) return;
    const timer = window.setTimeout(() => setBubble(null), PROFILE_BUBBLE_AUTO_HIDE_MS);
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setBubble(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [bubble]);

  const onAvatarClick = async () => {
    const previous = bubble?.text ?? null;
    let currentPool = pool;
    if (!poolReadyRef.current) {
      currentPool = (await refreshPool()) ?? [];
    } else {
      void refreshPool();
    }
    const name = userDisplay.displayName.trim();
    const text =
      pickRandomBubble(currentPool, previous) ?? `안녕하세요${name ? `, ${name}님` : ''}!`;
    setBubble({ text, key: Date.now() });
  };

  return (
    <div ref={wrapRef} className="cp-sidebar-user-greeting-avatar-wrap">
      <button
        type="button"
        className="cp-sidebar-user-greeting-avatar-link"
        aria-label="프로필 말풍선 보기"
        aria-expanded={bubble != null}
        onClick={() => void onAvatarClick()}
      >
        <UserProfileAvatar
          userId={userDisplay.userId}
          custom={
            userDisplay.profileImage
              ? { userId: userDisplay.userId, ...userDisplay.profileImage }
              : null
          }
          googleAvatarUrl={null}
          className="cp-sidebar-user-greeting-avatar"
        />
      </button>
      {onOpenClassSettings ? (
        <button
          type="button"
          className="cp-sidebar-avatar-corner cp-sidebar-avatar-corner--class"
          aria-label="클래스 등록 및 설정"
          title="클래스 등록 및 설정"
          onClick={() => {
            setBubble(null);
            onOpenClassSettings();
          }}
        >
          <span
            className="cp-sidebar-avatar-corner-class-icon"
            style={{
              WebkitMask: "url('/class.svg') center / contain no-repeat",
              mask: "url('/class.svg') center / contain no-repeat",
            }}
            aria-hidden="true"
          />
        </button>
      ) : null}
      {onOpenClassMembersSettings ? (
        <button
          type="button"
          className="cp-sidebar-avatar-corner cp-sidebar-avatar-corner--members"
          aria-label="클래스 멤버 설정"
          title="클래스 멤버 설정"
          onClick={() => {
            setBubble(null);
            onOpenClassMembersSettings();
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="8" r="3.2" />
            <path d="M6 19.2c.8-2.8 3-4.2 6-4.2s5.2 1.4 6 4.2" />
          </svg>
        </button>
      ) : null}
      {bubble ? (
        <div
          key={bubble.key}
          className="cp-profile-bubble cp-sidebar-profile-bubble"
          role="status"
          onClick={() => setBubble(null)}
        >
          {bubble.text}
        </div>
      ) : null}
    </div>
  );
}

/** 한 줄 이름이 넘치면 글자 크기를 줄여 줄임표 없이 가로에 맞춤 */
function SidebarFitDisplayName({ name }: { name: string }) {
  const label = name.trim() || '-';
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      el.style.fontSize = '';
      const computed = parseFloat(window.getComputedStyle(el).fontSize);
      const maxPx = Number.isFinite(computed) && computed > 0 ? computed : 14.7;
      const minPx = 9;
      if (el.scrollWidth <= el.clientWidth + 0.5) return;

      let lo = minPx;
      let hi = maxPx;
      let best = minPx;
      for (let i = 0; i < 14; i += 1) {
        const mid = (lo + hi) / 2;
        el.style.fontSize = `${mid}px`;
        if (el.scrollWidth <= el.clientWidth + 0.5) {
          best = mid;
          lo = mid;
        } else {
          hi = mid;
        }
      }
      el.style.fontSize = `${best}px`;
    };

    fit();
    const ro = new ResizeObserver(() => {
      fit();
    });
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [label]);

  return (
    <div ref={ref} className="cp-sidebar-user-greeting-name" title={label}>
      {label}
    </div>
  );
}

/** 클래스 사전 설정 (학사일정·수업시간·편제) — 통합 보기 지원 */
const classIndividualMenus: { to: string; label?: string; labelLines?: [string, string] }[] = [
  { to: '/academic-calendar', label: '학사일정' },
  { to: '/class-schedule', labelLines: ['수업시간 및 요일별', '기준 수업시수'] },
  { to: '/grade-track', label: '편제 및 기준 시수' },
];

/** 학습내용·평가계획 등록 (사용자 데이터는 선택 클래스별 독립) */
const classCommonMenus: { to: string; label: string }[] = [
  { to: '/workspace-learning-content', label: '기본 학습내용 등록' },
  { to: '/workspace-evaluation-plan', label: '기본 평가계획 등록' },
];

const CLASS_INDIVIDUAL_PATHS: readonly string[] = classIndividualMenus.map((m) => m.to);
const CLASS_COMMON_PATHS: readonly string[] = classCommonMenus.map((m) => m.to);
const PRESET_PATHS: readonly string[] = [...CLASS_INDIVIDUAL_PATHS, ...CLASS_COMMON_PATHS];

function injectGradeIdIntoTo(to: string, gradeId: string): string {
  const [path, query = ''] = to.split('?');
  const params = new URLSearchParams(query);
  params.set('gradeId', gradeId);
  const q = params.toString();
  return q ? `${path}?${q}` : path;
}

const PRESET_MENU_STATUS_DOT_PATHS = new Set([
  '/academic-calendar',
  '/class-schedule',
  '/grade-track',
]);

const PRESET_MENU_STATUS_KEY = {
  '/academic-calendar': 'academicCalendar',
  '/class-schedule': 'classSchedule',
  '/grade-track': 'gradeTrack',
} as const;

const adminMenuGroups: { to: string; label: string }[][] = [
  [
    { to: '/admin/users', label: '이용자 및 이용권 관리' },
    { to: '/admin/plan-permissions', label: '이용권별 권한 관리' },
    { to: '/admin/payment-options', label: '결제 옵션 설정' },
    { to: '/admin/beta-tester', label: '베타 테스트' },
  ],
  [
    { to: '/admin/landing-page', label: '랜딩 페이지 관리' },
    { to: '/admin/legal', label: '약관 및 개인정보처리방침 관리' },
    { to: '/admin/notices', label: '공지사항 관리' },
    { to: '/admin/messages', label: '메세지 발송 관리' },
    { to: '/admin/profile-bubbles', label: '프로필 말풍선 관리' },
    { to: '/admin/profile-cards', label: '프로필 카드 관리' },
  ],
  [
    { to: '/admin/learning-content', label: '학습내용 관리' },
    { to: '/admin/evaluation-plan', label: '평가계획 관리' },
    { to: '/admin/cross-curricular', label: '범교과 관리' },
    { to: '/admin/learning-plan', label: '활동계획 활동 관리' },
  ],
];

const adminMenus = adminMenuGroups.flat();
const managerAdminMenuGroup = adminMenuGroups[2] ?? [];

interface SideMenuProps {
  currentGradeId: string | null;
  userDisplay: UserDisplayInfo | null;
  onOpenClassSettings?: () => void;
  onOpenClassMembersSettings?: () => void;
}

function isTodoNoteRoute(path: string): boolean {
  return path === '/todo-note' || path.startsWith('/todo-note/');
}

function isBudgetNoteRoute(path: string): boolean {
  return (
    path === '/budget-note' ||
    path.startsWith('/budget-note/') ||
    path === '/admin/budget-note' ||
    path.startsWith('/admin/budget-note/')
  );
}

function isAssignmentPointsNoteRoute(path: string): boolean {
  return (
    path === '/assignment-note' ||
    path.startsWith('/assignment-note/') ||
    path === '/assignment-points-note' ||
    path.startsWith('/assignment-points-note/')
  );
}

function isPointItemNoteRoute(path: string): boolean {
  return path === '/point-item-note' || path.startsWith('/point-item-note/');
}

/** 교육과정 노트(클래스 선택 이하) 하위 경로 */
function isCurriculumNoteRoute(path: string): boolean {
  if (path === '/class-record-memos' || path.startsWith('/class-record-memos/')) return true;
  if (path === '/weekly-learning-guide' || path.startsWith('/weekly-learning-guide/')) return true;
  if (PRESET_PATHS.includes(path)) return true;
  if (path === '/annual-timetable' || path.startsWith('/annual-timetable/')) return true;
  if (path.startsWith('/annual-progress')) return true;
  if (path.startsWith('/program/')) return true;
  return false;
}

const SideMenu: React.FC<SideMenuProps> = ({
  currentGradeId,
  userDisplay,
  onOpenClassSettings,
  onOpenClassMembersSettings,
}) => {
  const { isFeatureLocked, requireFeature } = usePaidPassGate();
  const passLockClassFor = (key: PlanFeatureKey) => (isFeatureLocked(key) ? ' is-pass-locked' : '');
  const menuLockClass = (to: string) => {
    const key = planFeatureForPath(to.split('?')[0]);
    return key ? passLockClassFor(key) : '';
  };
  const location = useLocation();
  const [isBetaTester, setIsBetaTester] = useState(false);
  useEffect(() => {
    const refresh = () => {
      const uid = getBoundSubscriptionPassUserId();
      setIsBetaTester(hasActiveBetaTesterPass(loadSubscriptionPassState(uid)));
    };
    refresh();
    window.addEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, refresh);
  }, []);
  const isCurriculumSubMenuActive = React.useMemo(
    () => isCurriculumNoteRoute(location.pathname),
    [location.pathname]
  );
  const navigate = useNavigate();
  const {
    programFocusGradeId,
    grades,
    workspaceId,
    programSidebarAcademicYear,
    selectProgramFocusGrade,
  } = useProgramSidebar();
  const {
    classMembersDirty,
    gradeTrackDirty,
    academicCalendarDirty,
    classScheduleDirty,
    annualTimetableDirty,
    adminLearningDirty,
    workspaceLearningContentDirty,
    workspaceEvaluationPlanDirty,
    todoNoteDirty,
    budgetNoteDirty,
    assignmentPointsNoteDirty,
    evaluationPlanDirty,
    setPendingLeaveTo,
    programProgressDirty,
    setProgramPendingLeaveTo,
    sidebarTracksEpoch,
  } = useContext(GradeTrackLeaveContext);

  const [tracks, setTracks] = useState<Track[]>([]);
  const [gradeTrackSections, setGradeTrackSections] = useState<PageSectionLayout[]>([]);
  const [presetSettingsOpen, setPresetSettingsOpen] = useState(false);
  const [classCommonSettingsOpen, setClassCommonSettingsOpen] = useState(false);
  const initialSidebarProgramExpand = useMemo(() => loadSidebarProgramExpand(), []);
  const [fixedProgramOpen, setFixedProgramOpen] = useState(initialSidebarProgramExpand.fixed);
  const [projectOpen, setProjectOpen] = useState(initialSidebarProgramExpand.project);
  const [trackProgramsOpen, setTrackProgramsOpen] = useState(initialSidebarProgramExpand.track);
  const [annualProjectMenuRev, setAnnualProjectMenuRev] = useState(0);
  const [trackSplitRev, setTrackSplitRev] = useState(0);
  const [classPickerOpen, setClassPickerOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [sessionEmail, setSessionEmail] = useState('');
  const [isPlatformManager, setIsPlatformManager] = useState(false);
  const [adminSidebarMenuVisible, setAdminSidebarMenuVisible] = useState(true);
  const [classRequiredModal, setClassRequiredModal] = useState<{ pendingTo: string | null } | null>(
    null
  );
  const [classRequiredModalPickerOpen, setClassRequiredModalPickerOpen] = useState(false);
  const classPickerWrapRef = useRef<HTMLDivElement>(null);
  const classRequiredModalPickerRef = useRef<HTMLDivElement>(null);
  /** 저장된 펼침이 있으면 첫 경로 동기화에서 덮어쓰지 않음 */
  const programExpandPathSyncedRef = useRef(false);
  /** 사용자가 클래스 사전 설정을 직접 접거나 펼치면 기본 펼침 로직을 덮지 않음 */
  const presetUserToggledRef = useRef(false);

  const isAdminUser = useMemo(() => {
    const displayEmail = (userDisplay?.email ?? '').trim().toLowerCase();
    return isPlatformAdminEmail(displayEmail) || isPlatformAdminEmail(sessionEmail);
  }, [userDisplay?.email, sessionEmail]);

  const adminPrefEmail = useMemo(() => {
    const displayEmail = (userDisplay?.email ?? '').trim().toLowerCase();
    return displayEmail || sessionEmail;
  }, [userDisplay?.email, sessionEmail]);

  const canSeeAdminMenu =
    (isAdminUser || isPlatformManager) && adminSidebarMenuVisible;

  const visibleAdminMenuGroups = useMemo(() => {
    if (isAdminUser) return adminMenuGroups;
    if (isPlatformManager) return [managerAdminMenuGroup];
    return [];
  }, [isAdminUser, isPlatformManager]);

  /** 관리자 펼침 트리거: 하위 메뉴만 활성(예산·ToDo 노트 경로와 무관) */
  const isAdminSubMenuActive = useMemo(() => {
    const path = location.pathname;
    if (isTodoNoteRoute(path)) return false;
    if (isBudgetNoteRoute(path)) return false;
    if (isAssignmentPointsNoteRoute(path)) return false;
    if (isPointItemNoteRoute(path)) return false;
    if (isAdminUser) {
      return adminMenus.some((menu) => path === menu.to || path.startsWith(`${menu.to}/`));
    }
    if (isPlatformManager) {
      return PLATFORM_MANAGER_ADMIN_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
    }
    return false;
  }, [location.pathname, isAdminUser, isPlatformManager]);

  const isIntegratedClassView = !programFocusGradeId;
  const presetMenuGradeStatus = usePresetMenuGradeStatus({
    workspaceId,
    gradeId: currentGradeId,
    gradeTrackSections,
    tracks,
    refreshEpoch: sidebarTracksEpoch,
  });
  const presetHasIncompleteData = Boolean(currentGradeId) &&
    !(
      presetMenuGradeStatus.academicCalendar &&
      presetMenuGradeStatus.classSchedule &&
      presetMenuGradeStatus.gradeTrack &&
      presetMenuGradeStatus.classMembers
    );

  useEffect(() => {
    presetUserToggledRef.current = false;
  }, [currentGradeId]);

  useEffect(() => {
    if (CLASS_INDIVIDUAL_PATHS.includes(location.pathname)) {
      setPresetSettingsOpen(true);
      return;
    }
    if (presetUserToggledRef.current) return;
    setPresetSettingsOpen(presetHasIncompleteData);
  }, [location.pathname, presetHasIncompleteData]);
  const focusGradeRow = useMemo(
    () => (programFocusGradeId ? grades.find((g) => g.id === programFocusGradeId) ?? null : null),
    [grades, programFocusGradeId]
  );

  const focusClassParsed = useMemo(() => {
    if (!focusGradeRow) return null;
    return parseGradeDisplayName(focusGradeRow.name);
  }, [focusGradeRow]);

  const gradesSortedForPicker = useMemo(
    () =>
      [...grades].sort((a, b) =>
        parseGradeDisplayName(a.name).title.localeCompare(parseGradeDisplayName(b.name).title, 'ko')
      ),
    [grades]
  );

  const applySidebarClassSelection = useCallback(
    (gradeId: string) => {
      const id = gradeId.trim();
      if (!id) return;
      selectProgramFocusGrade(id);
      setClassPickerOpen(false);
      const params = new URLSearchParams(location.search);
      if (params.has('gradeId')) {
        params.set('gradeId', id);
        const q = params.toString();
        navigate({ pathname: location.pathname, search: q ? `?${q}` : '' }, { replace: true });
      }
    },
    [location.pathname, location.search, navigate, selectProgramFocusGrade]
  );

  const closeClassRequiredModal = useCallback(() => {
    setClassRequiredModal(null);
    setClassRequiredModalPickerOpen(false);
  }, []);

  /** pendingTo null이면 클래스만 선택하고 이동하지 않음 */
  const openClassRequiredModal = useCallback((pendingTo: string | null) => {
    setClassRequiredModal({ pendingTo });
    setClassRequiredModalPickerOpen(false);
  }, []);

  const navigateAfterClassPick = useCallback(
    (to: string) => {
      const from = location.pathname;
      const [toPath] = to.split('?');
      const leavingProgramProgress =
        isProgramProgressFamilyRoute(from) && !isProgramProgressFamilyRoute(toPath);
      if (leavingProgramProgress && isProgramProgressFamilyLeaveDirty(programProgressDirty)) {
        setProgramPendingLeaveTo(to);
        return;
      }
      const dirty =
        (from === '/class-members' && classMembersDirty) ||
        (from === '/grade-track' && gradeTrackDirty) ||
        (from === '/academic-calendar' && academicCalendarDirty) ||
        (from === '/class-schedule' && classScheduleDirty) ||
        (from === '/workspace-learning-content' && workspaceLearningContentDirty) ||
        (from === '/workspace-evaluation-plan' && workspaceEvaluationPlanDirty) ||
        (from === '/annual-timetable' && annualTimetableDirty) ||
        (from === '/admin/learning-content' && adminLearningDirty) ||
        (from === '/admin/evaluation-plan' && evaluationPlanDirty) ||
        (isTodoNoteRoute(from) && todoNoteDirty) ||
        (isBudgetNoteRoute(from) && budgetNoteDirty) ||
        (isAssignmentPointsNoteRoute(from) && assignmentPointsNoteDirty);
      if (dirty) {
        setPendingLeaveTo(to);
        return;
      }
      navigate(to);
    },
    [
      academicCalendarDirty,
      adminLearningDirty,
      annualTimetableDirty,
      assignmentPointsNoteDirty,
      budgetNoteDirty,
      classMembersDirty,
      classScheduleDirty,
      evaluationPlanDirty,
      gradeTrackDirty,
      location.pathname,
      navigate,
      programProgressDirty,
      setPendingLeaveTo,
      setProgramPendingLeaveTo,
      todoNoteDirty,
      workspaceEvaluationPlanDirty,
      workspaceLearningContentDirty,
    ]
  );

  const confirmClassRequiredAndOpen = useCallback(
    (gradeId: string) => {
      if (!gradeId || !classRequiredModal) return;
      selectProgramFocusGrade(gradeId);
      const pendingTo = classRequiredModal.pendingTo;
      closeClassRequiredModal();
      if (pendingTo) {
        navigateAfterClassPick(injectGradeIdIntoTo(pendingTo, gradeId));
      }
    },
    [classRequiredModal, closeClassRequiredModal, navigateAfterClassPick, selectProgramFocusGrade]
  );

  useEffect(() => {
    if (!classPickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = classPickerWrapRef.current;
      if (el && !el.contains(e.target as Node)) setClassPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setClassPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [classPickerOpen]);

  useEffect(() => {
    if (!classRequiredModal || !classRequiredModalPickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = classRequiredModalPickerRef.current;
      if (el && !el.contains(e.target as Node)) setClassRequiredModalPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [classRequiredModal, classRequiredModalPickerOpen]);

  useEffect(() => {
    if (!classRequiredModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (classRequiredModalPickerOpen) {
        setClassRequiredModalPickerOpen(false);
        return;
      }
      closeClassRequiredModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [classRequiredModal, classRequiredModalPickerOpen, closeClassRequiredModal]);

  const effectiveSidebarAcademicYear = programSidebarAcademicYear ?? new Date().getFullYear();
  useEffect(() => {
    let cancelled = false;
    void getSession()
      .then((session) => {
        if (cancelled) return;
        setSessionEmail((session?.user?.email ?? '').trim().toLowerCase());
      })
      .catch(() => {
        if (cancelled) return;
        setSessionEmail('');
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isAdminUser) {
      setIsPlatformManager(false);
      return;
    }
    let cancelled = false;
    void fetchAmIPlatformManager()
      .then((v) => {
        if (!cancelled) setIsPlatformManager(v);
      })
      .catch(() => {
        if (!cancelled) setIsPlatformManager(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdminUser, sessionEmail, userDisplay?.email]);

  useEffect(() => {
    if (!adminPrefEmail) {
      setAdminSidebarMenuVisible(true);
      return;
    }
    setAdminSidebarMenuVisible(readAdminSidebarMenuVisiblePref(adminPrefEmail));
  }, [adminPrefEmail]);

  useEffect(() => {
    const onPref = (ev: Event) => {
      const detail = (ev as CustomEvent<{ email?: string; visible?: boolean }>).detail;
      if (detail?.email && adminPrefEmail && detail.email !== adminPrefEmail) return;
      if (typeof detail?.visible === 'boolean') {
        setAdminSidebarMenuVisible(detail.visible);
        return;
      }
      if (adminPrefEmail) {
        setAdminSidebarMenuVisible(readAdminSidebarMenuVisiblePref(adminPrefEmail));
      }
    };
    window.addEventListener(ADMIN_SIDEBAR_MENU_VISIBLE_CHANGED_EVENT, onPref);
    return () => window.removeEventListener(ADMIN_SIDEBAR_MENU_VISIBLE_CHANGED_EVENT, onPref);
  }, [adminPrefEmail]);

  const [swapTermOrder, setSwapTermOrder] = useState(() => loadAnnualSwapTermOrder(workspaceId));
  useEffect(() => {
    if (!workspaceId) return;
    setSwapTermOrder(loadAnnualSwapTermOrder(workspaceId));
  }, [workspaceId]);
  useEffect(() => {
    const onSwapTermOrder = (ev: Event) => {
      const detail = (ev as CustomEvent<{ workspaceId?: string; value?: boolean }>).detail;
      if (detail?.workspaceId && detail.workspaceId !== workspaceId) return;
      if (typeof detail?.value === 'boolean') {
        setSwapTermOrder(detail.value);
        return;
      }
      setSwapTermOrder(loadAnnualSwapTermOrder(workspaceId));
    };
    window.addEventListener(ANNUAL_SWAP_TERM_ORDER_CHANGED_EVENT, onSwapTermOrder);
    return () => window.removeEventListener(ANNUAL_SWAP_TERM_ORDER_CHANGED_EVENT, onSwapTermOrder);
  }, [workspaceId]);

  const projectMenuItems = useMemo(() => {
    void annualProjectMenuRev; /* 프로젝트 저장 시 사이드바 목록 재계산 */
    if (!workspaceId || !programFocusGradeId) return [];
    const list = loadAnnualProjectPrograms(workspaceId, programFocusGradeId, effectiveSidebarAcademicYear);
    return list.slice().sort((a, b) => {
      if (a.termNumber !== b.termNumber) {
        return swapTermOrder ? b.termNumber - a.termNumber : a.termNumber - b.termNumber;
      }
      return a.name.localeCompare(b.name, 'ko');
    });
  }, [workspaceId, programFocusGradeId, effectiveSidebarAcademicYear, annualProjectMenuRev, swapTermOrder]);

  const tracksForProgramMenu = useMemo(() => {
    if (!programFocusGradeId) return [];
    return filterTracksSubscribedToGradeTrackSections(
      gradeTrackSections,
      programFocusGradeId,
      tracks
    );
  }, [gradeTrackSections, programFocusGradeId, tracks]);

  /** 편제 및 기준 시수에서 저장한 편제 순서(동일 localStorage) */
  const trackNamesOrderedForSidebar = useMemo(() => {
    if (!workspaceId || !programFocusGradeId) return [];
    return getOrderedTrackNamesForGrade(
      workspaceId,
      programFocusGradeId,
      tracks,
      gradeTrackSections
    );
  }, [workspaceId, programFocusGradeId, tracks, gradeTrackSections]);

  /** 1학기 전체 → 2학기 전체 순 (학기별 그룹). 위치 바꾸기 on이면 2학기가 먼저. */
  const trackMenuRows = useMemo(() => {
    void trackSplitRev; // 시간표명별 분리 on/off 변경 시 재계산
    if (!workspaceId || !programFocusGradeId) return [];
    const modeMap = loadTrackProgramModeMap(workspaceId, programFocusGradeId);
    const aliasOrderMap = loadTrackAliasOrderMap(workspaceId, programFocusGradeId);
    const rowsByTrack = trackNamesOrderedForSidebar.flatMap((name) => {
      const aliases = Array.from(
        new Set(
          tracksForProgramMenu
            .filter((t) => (t.name ?? '').trim() === name)
            .map((t) => (t.description ?? '').trim())
            .filter(Boolean)
        )
      );
      const storedAliasOrder = aliasOrderMap[name] ?? [];
      const orderedAliases =
        storedAliasOrder.length > 0
          ? [
              ...storedAliasOrder.filter((alias) => aliases.includes(alias)),
              ...aliases.filter((alias) => !storedAliasOrder.includes(alias)),
            ]
          : aliases;
      const mode = modeMap[name] ?? 'unified';
      const splitOrSync = (mode === 'split' || mode === 'sync') && aliases.length >= 2;
      if (!splitOrSync) {
        return [{ name, label: name, timetableName: null as string | null, isSyncRep: false }];
      }
      const syncRepMap = mode === 'sync' ? loadTrackProgramSyncRepMap(workspaceId, programFocusGradeId) : {};
      const repAlias = mode === 'sync' ? (syncRepMap[name] ?? orderedAliases[0] ?? '') : '';
      return orderedAliases.map((alias) => ({
        name,
        label: alias || name,
        timetableName: alias,
        isSyncRep: mode === 'sync' && alias === repAlias,
      }));
    });
    const term1 = rowsByTrack.map((row) => ({ ...row, term: 1 as const }));
    const term2 = rowsByTrack.map((row) => ({ ...row, term: 2 as const }));
    return swapTermOrder ? [...term2, ...term1] : [...term1, ...term2];
  }, [programFocusGradeId, workspaceId, trackNamesOrderedForSidebar, tracksForProgramMenu, trackSplitRev, swapTermOrder]);

  useEffect(() => {
    const onProgramsChanged = () => setAnnualProjectMenuRev((r) => r + 1);
    window.addEventListener(ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT, onProgramsChanged);
    return () => window.removeEventListener(ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT, onProgramsChanged);
  }, []);

  useEffect(() => {
    if (!workspaceId || !programFocusGradeId) return;
    const localProjects = loadAnnualProjectPrograms(workspaceId, programFocusGradeId, effectiveSidebarAcademicYear);
    if (localProjects.length > 0) return;
    void getAnnualProjectPrograms(workspaceId, programFocusGradeId, effectiveSidebarAcademicYear).catch(
      () => undefined
    );
  }, [workspaceId, programFocusGradeId, effectiveSidebarAcademicYear]);

  useEffect(() => {
    const onTrackSplitChanged = () => setTrackSplitRev((r) => r + 1);
    window.addEventListener(TRACK_PROGRAM_SPLIT_CHANGE_EVENT, onTrackSplitChanged);
    return () => window.removeEventListener(TRACK_PROGRAM_SPLIT_CHANGE_EVENT, onTrackSplitChanged);
  }, []);

  /** 서버에 저장된 편제 프로그램 모드·동기화 대표명을 localStorage에 맞춰 두어 새로고침 후에도 사이드바·프로그램 화면이 동일하게 동작하도록 함 */
  useEffect(() => {
    if (!workspaceId || grades.length === 0) return;
    let cancelled = false;
    void getGradeTrackProgramPreferences(workspaceId)
      .then((prefs) => {
        if (cancelled) return;
        /** 엄격 모드: DB prefs 거울로 교체(병합 시 편제 및 기준 시수에서 지운 설정이 되살아남) */
        let touched = false;
        grades.forEach((g) => {
          if (applyGradeTrackProgramPrefsToLocalStores(workspaceId, g.id, prefs)) touched = true;
        });
        if (touched) setTrackSplitRev((r) => r + 1);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [workspaceId, grades]);

  useEffect(() => {
    if (!workspaceId) {
      setTracks([]);
      setGradeTrackSections([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [list, sections] = await Promise.all([
          getTracks(workspaceId),
          getGradeTrackSections(workspaceId).catch(() => [{ id: 'base', gradeIds: [] }] as PageSectionLayout[]),
        ]);
        if (!cancelled) {
          setTracks(list);
          setGradeTrackSections(sections);
        }
      } catch {
        if (!cancelled) {
          setTracks([]);
          setGradeTrackSections([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, sidebarTracksEpoch, location.pathname]);

  useEffect(() => {
    if (CLASS_COMMON_PATHS.includes(location.pathname)) {
      setClassCommonSettingsOpen(true);
    }
    if (isAdminSubMenuActive) {
      setAdminMenuOpen(true);
    }

    const applyProgramExpandFromPath = () => {
      if (location.pathname === '/annual-timetable' || location.pathname === '/program/fixed') {
        setFixedProgramOpen(true);
      }
      if (location.pathname.startsWith('/program/track/')) {
        setTrackProgramsOpen(true);
      }
      if (location.pathname.startsWith('/program/project')) {
        setProjectOpen(true);
      }
    };

    if (!programExpandPathSyncedRef.current) {
      programExpandPathSyncedRef.current = true;
      if (!hasSidebarProgramExpandStored()) {
        applyProgramExpandFromPath();
      }
      return;
    }

    applyProgramExpandFromPath();
  }, [location.pathname, isAdminSubMenuActive]);

  useEffect(() => {
    saveSidebarProgramExpand({
      fixed: fixedProgramOpen,
      project: projectOpen,
      track: trackProgramsOpen,
    });
  }, [fixedProgramOpen, projectOpen, trackProgramsOpen]);

  const handleMenuClick = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    const [toPath, toQuery] = to.split('?');
    const paidFeature = planFeatureForPath(toPath);
    if (paidFeature && requireFeature(paidFeature)) {
      e.preventDefault();
      return;
    }
    if (isIntegratedClassView && pathRequiresSingleClassSelection(toPath)) {
      e.preventDefault();
      openClassRequiredModal(to);
      return;
    }
    const searchMatch = toQuery ? location.search === `?${toQuery}` : location.search === '';
    if (location.pathname === toPath && searchMatch) return;
    const from = location.pathname;
    const leavingProgramProgress =
      isProgramProgressFamilyRoute(from) && !isProgramProgressFamilyRoute(toPath);
    if (leavingProgramProgress && isProgramProgressFamilyLeaveDirty(programProgressDirty)) {
      e.preventDefault();
      setProgramPendingLeaveTo(to);
      return;
    }
    const dirty =
      (from === '/class-members' && classMembersDirty) ||
      (from === '/grade-track' && gradeTrackDirty) ||
      (from === '/academic-calendar' && academicCalendarDirty) ||
      (from === '/class-schedule' && classScheduleDirty) ||
      (from === '/workspace-learning-content' && workspaceLearningContentDirty) ||
      (from === '/workspace-evaluation-plan' && workspaceEvaluationPlanDirty) ||
      (from === '/annual-timetable' && annualTimetableDirty) ||
      (from === '/admin/learning-content' && adminLearningDirty) ||
      (from === '/admin/evaluation-plan' && evaluationPlanDirty) ||
      (isTodoNoteRoute(from) && todoNoteDirty) ||
      (isBudgetNoteRoute(from) && budgetNoteDirty) ||
      (isAssignmentPointsNoteRoute(from) && assignmentPointsNoteDirty);
    if (dirty) {
      e.preventDefault();
      setPendingLeaveTo(to);
    }
  };

  const classIndividualSectionActive = CLASS_INDIVIDUAL_PATHS.includes(location.pathname);
  const classCommonSectionActive = CLASS_COMMON_PATHS.includes(location.pathname);
  const fixedProgramSectionActive =
    location.pathname === '/annual-timetable' || location.pathname === '/program/fixed';
  const projectSectionActive = location.pathname.startsWith('/program/project');
  const trackSectionActive = location.pathname.startsWith('/program/track/');

  const annualProgressTo = (termPath: '/annual-progress-term1' | '/annual-progress-term2') => {
    const params: Record<string, string> = { openToday: String(Date.now()) };
    if (programFocusGradeId) params.gradeId = programFocusGradeId;
    return `${termPath}?${createSearchParams(params).toString()}`;
  };

  /** 연간진도표 메뉴: Shift+R과 동일 — 이미 해당 학기·학년이면 합성 Shift+T로 현재 시각 교시만 선택, 아니면 openToday 포함 이동 */
  const onAnnualProgressTermNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    termPath: '/annual-progress-term1' | '/annual-progress-term2'
  ) => {
    const to = annualProgressTo(termPath);
    handleMenuClick(e, to);
    if (e.defaultPrevented) return;
    const [toPath, toQuery] = to.split('?');
    const targetGrade = new URLSearchParams(toQuery ?? '').get('gradeId')?.trim() ?? '';
    const curGrade = new URLSearchParams(location.search).get('gradeId')?.trim() ?? '';
    if (location.pathname === toPath && targetGrade === curGrade) {
      e.preventDefault();
      document.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'T',
          code: 'KeyT',
          shiftKey: true,
          bubbles: true,
          cancelable: true,
        })
      );
    }
  };

  /** 교육과정 노트: 오늘 기준 해당 학기 연간진도표로 이동 (Shift+R과 동일) */
  const onCurriculumNoteClick = async () => {
    const todayIso = toISODate(new Date());
    const currentYear = new Date().getFullYear();
    let targetTerm: 1 | 2 = Number(todayIso.slice(5, 7)) >= 8 ? 2 : 1;
    try {
      const terms = await getAcademicTerms(workspaceId, currentYear);
      const inRange = terms.find(
        (term) =>
          todayIso >= term.starts_on &&
          todayIso <= term.ends_on &&
          (term.term_number === 1 || term.term_number === 2)
      );
      if (inRange) targetTerm = inRange.term_number;
    } catch {
      // 학기 조회 실패 시 월 기준 추정 학기 사용
    }
    const termPath = targetTerm === 1 ? '/annual-progress-term1' : '/annual-progress-term2';
    const to = annualProgressTo(termPath);
    if (isIntegratedClassView) {
      openClassRequiredModal(to);
      return;
    }
    const [toPath, toQuery] = to.split('?');
    const targetGrade = new URLSearchParams(toQuery ?? '').get('gradeId')?.trim() ?? '';
    const curGrade = new URLSearchParams(location.search).get('gradeId')?.trim() ?? '';
    if (location.pathname === toPath && targetGrade === curGrade) {
      document.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'T',
          code: 'KeyT',
          shiftKey: true,
          bubbles: true,
          cancelable: true,
        })
      );
      return;
    }
    const from = location.pathname;
    const leavingProgramProgress =
      isProgramProgressFamilyRoute(from) && !isProgramProgressFamilyRoute(toPath);
    if (leavingProgramProgress && isProgramProgressFamilyLeaveDirty(programProgressDirty)) {
      setProgramPendingLeaveTo(to);
      return;
    }
    const dirty =
      (from === '/class-members' && classMembersDirty) ||
      (from === '/grade-track' && gradeTrackDirty) ||
      (from === '/academic-calendar' && academicCalendarDirty) ||
      (from === '/class-schedule' && classScheduleDirty) ||
      (from === '/workspace-learning-content' && workspaceLearningContentDirty) ||
      (from === '/workspace-evaluation-plan' && workspaceEvaluationPlanDirty) ||
      (from === '/annual-timetable' && annualTimetableDirty) ||
      (from === '/admin/learning-content' && adminLearningDirty) ||
      (from === '/admin/evaluation-plan' && evaluationPlanDirty) ||
      (isTodoNoteRoute(from) && todoNoteDirty) ||
      (isBudgetNoteRoute(from) && budgetNoteDirty) ||
      (isAssignmentPointsNoteRoute(from) && assignmentPointsNoteDirty);
    if (dirty) {
      setPendingLeaveTo(to);
      return;
    }
    navigate(to);
  };
  const recordMemosTo = () => {
    const path = '/class-record-memos';
    if (!programFocusGradeId) return path;
    return `${path}?${createSearchParams({ gradeId: programFocusGradeId }).toString()}`;
  };

  const weeklyLearningGuideTo = () => {
    const path = '/weekly-learning-guide';
    if (!programFocusGradeId) return path;
    return `${path}?${createSearchParams({ gradeId: programFocusGradeId }).toString()}`;
  };

  const fixedProgramTermTo = (termNum: 1 | 2) => {
    const params: Record<string, string> = {
      programTerm: String(termNum),
      programYear: String(effectiveSidebarAcademicYear),
    };
    if (programFocusGradeId) params.gradeId = programFocusGradeId;
    return `/program/fixed?${createSearchParams(params).toString()}`;
  };

  const fixedTermIsActive = (termNum: 1 | 2) => {
    if (location.pathname !== '/program/fixed') return false;
    const q = new URLSearchParams(location.search);
    return q.get('programTerm') === String(termNum);
  };

  const trackLinkTo = (trackName: string, termNum: 1 | 2, timetableName?: string | null) => {
    const path = `/program/track/${encodeURIComponent(trackName)}`;
    const params: Record<string, string> = {
      programTerm: String(termNum),
      programYear: String(effectiveSidebarAcademicYear),
    };
    if (programFocusGradeId) params.gradeId = programFocusGradeId;
    if (timetableName) params.programTimetableName = timetableName;
    return `${path}?${createSearchParams(params).toString()}`;
  };

  const trackLinkIsActive = (trackName: string, termNum: 1 | 2, timetableName?: string | null) => {
    if (location.pathname !== `/program/track/${encodeURIComponent(trackName)}`) return false;
    const q = new URLSearchParams(location.search);
    return (
      q.get('programTerm') === String(termNum) &&
      (q.get('programTimetableName') ?? '') === (timetableName ?? '')
    );
  };

  const projectDetailTo = (projectId: string, termNumber: 1 | 2) => {
    const path = `/program/project/${encodeURIComponent(projectId)}`;
    const params: Record<string, string> = {
      programTerm: String(termNumber),
      programYear: String(effectiveSidebarAcademicYear),
    };
    if (programFocusGradeId) params.gradeId = programFocusGradeId;
    return `${path}?${createSearchParams(params).toString()}`;
  };

  const projectLinkIsActive = (projectId: string, termNumber: 1 | 2) => {
    if (!location.pathname.startsWith('/program/project/')) return false;
    const rest = location.pathname.slice('/program/project/'.length);
    if (decodeURIComponent(rest) !== projectId && rest !== projectId) return false;
    const q = new URLSearchParams(location.search);
    return q.get('programTerm') === String(termNumber);
  };

  return (
    <aside className="cp-sidebar">
      <nav className="cp-sidebar-nav" aria-label="주 메뉴">
        <div className="cp-sidebar-top-zone">
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            `cp-sidebar-app-title-link${isActive ? ' is-active' : ''}`
          }
          title="대시보드 (Shift+H)"
          aria-label={APP_DISPLAY_NAME}
          onClick={(e) => handleMenuClick(e, '/dashboard')}
        >
          <BrandLogo className="cp-brand-logo--sidebar" />
        </NavLink>
        <div className="cp-sidebar-primary-panel">
          {userDisplay ? (
            <div className="cp-sidebar-user-greeting" aria-label="이용자 정보">
              <SidebarProfileAvatarBubble
                userDisplay={userDisplay}
                onOpenClassSettings={onOpenClassSettings}
                onOpenClassMembersSettings={onOpenClassMembersSettings}
              />
              <div className="cp-sidebar-user-greeting-text">
                <div
                  className="cp-sidebar-user-greeting-team"
                  title={
                    userDisplay.hasActiveTeam
                      ? `${userDisplay.teamDisplayName} ${userDisplay.roleLabel}`
                      : '개인 워크스페이스'
                  }
                >
                  {userDisplay.hasActiveTeam ? (
                    <>
                      <span>{userDisplay.teamDisplayName}</span>
                      <span>{userDisplay.roleLabel}</span>
                    </>
                  ) : (
                    <span>개인</span>
                  )}
                </div>
                <div className="cp-sidebar-user-greeting-name-wrap">
                  <SidebarFitDisplayName name={userDisplay.displayName} />
                </div>
              </div>
            </div>
          ) : null}
          {canSeeAdminMenu ? (
            <div className="cp-menu-expandable cp-sidebar-admin-expandable">
              <div className="cp-sidebar-admin-vertical-stack">
                <button
                  type="button"
                  className={`cp-sidebar-note-tile cp-sidebar-note-tile--wide cp-sidebar-admin-menu-trigger${isAdminSubMenuActive ? ' active' : ''}`}
                  aria-expanded={adminMenuOpen}
                  aria-controls="cp-sidebar-admin-sub"
                  id="cp-sidebar-admin-trigger"
                  onClick={() => setAdminMenuOpen((o) => !o)}
                >
                  <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="admin" /></span>
                  <span className="cp-sidebar-note-tile-label">관리자 메뉴</span>
                  <span
                    className={`cp-menu-expandable-chevron cp-sidebar-admin-menu-chevron${adminMenuOpen ? ' is-open' : ''}`}
                    aria-hidden="true"
                  >
                    ›
                  </span>
                </button>
                {adminMenuOpen ? (
                  <div
                    id="cp-sidebar-admin-sub"
                    className="cp-menu-sublist cp-sidebar-admin-sublist"
                    role="group"
                    aria-labelledby="cp-sidebar-admin-trigger"
                  >
                    {visibleAdminMenuGroups.map((group, groupIdx) => (
                      <div
                        key={`admin-menu-group-${groupIdx}`}
                        className="cp-sidebar-admin-sublist-group"
                      >
                        {group.map((menu) => (
                          <NavLink
                            key={menu.to}
                            to={menu.to}
                            className={({ isActive }) =>
                              `cp-menu-item cp-menu-sub-item cp-sidebar-admin-sublink${isActive ? ' active' : ''}`
                            }
                            onClick={(e) => handleMenuClick(e, menu.to)}
                          >
                            {menu.label}
                          </NavLink>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="cp-sidebar-workspace-top-cluster">
            <div
              className="cp-sidebar-workspace-notes-stack"
              role="group"
              aria-label="워크스페이스 노트"
            >
              <div className="cp-sidebar-note-tiles">
                <NavLink
                  to="/dashboard"
                  end
                  className={({ isActive }) =>
                    `cp-sidebar-note-tile cp-sidebar-note-tile--wide cp-sidebar-note-tile--dashboard${isActive ? ' active' : ''}`
                  }
                  title="대시보드 (Shift+H)"
                  onClick={(e) => handleMenuClick(e, '/dashboard')}
                >
                  <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="dashboard" /></span>
                  <span className="cp-sidebar-note-tile-label">대시보드</span>
                </NavLink>
                {isBetaTester ? (
                  <NavLink
                    to="/beta-tester"
                    end
                    className={({ isActive }) =>
                      `cp-sidebar-note-tile cp-sidebar-note-tile--wide${isActive ? ' active' : ''}`
                    }
                    onClick={(e) => handleMenuClick(e, '/beta-tester')}
                  >
                    <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="beta" /></span>
                    <span className="cp-sidebar-note-tile-label">베타 테스터 노트</span>
                  </NavLink>
                ) : null}
                <NavLink
                  to="/daily-note"
                  end
                  className={({ isActive }) =>
                    `cp-sidebar-note-tile cp-sidebar-note-tile--daily${isActive ? ' active' : ''}${passLockClassFor('daily_note')}`
                  }
                  onClick={(e) => handleMenuClick(e, '/daily-note')}
                  aria-disabled={isFeatureLocked('daily_note') || undefined}
                >
                  <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="daily" /></span>
                  <span className="cp-sidebar-note-tile-label">데일리 노트</span>
                </NavLink>
                <NavLink
                  to="/todo-note"
                  end
                  className={({ isActive }) =>
                    `cp-sidebar-note-tile cp-sidebar-note-tile--todo${isActive ? ' active' : ''}${passLockClassFor('todo_note')}`
                  }
                  onClick={(e) => handleMenuClick(e, '/todo-note')}
                  aria-disabled={isFeatureLocked('todo_note') || undefined}
                >
                  <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="todo" /></span>
                  <span className="cp-sidebar-note-tile-label">ToDo 노트</span>
                </NavLink>
                <NavLink
                  to="/assignment-note"
                  end
                  className={({ isActive }) =>
                    `cp-sidebar-note-tile cp-sidebar-note-tile--assignment${isActive ? ' active' : ''}${passLockClassFor('assignment_note')}`
                  }
                  onClick={(e) => handleMenuClick(e, '/assignment-note')}
                  aria-disabled={isFeatureLocked('assignment_note') || undefined}
                >
                  <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="assignment" /></span>
                  <span className="cp-sidebar-note-tile-label">과제 노트</span>
                </NavLink>
                <NavLink
                  to="/point-item-note"
                  end
                  className={({ isActive }) =>
                    `cp-sidebar-note-tile cp-sidebar-note-tile--point${isActive ? ' active' : ''}${passLockClassFor('point_item_note')}`
                  }
                  onClick={(e) => handleMenuClick(e, '/point-item-note')}
                  aria-disabled={isFeatureLocked('point_item_note') || undefined}
                >
                  <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="point" /></span>
                  <span className="cp-sidebar-note-tile-label">포인트 노트</span>
                </NavLink>
                <NavLink
                  to="/budget-note"
                  end
                  className={({ isActive }) =>
                    `cp-sidebar-note-tile cp-sidebar-note-tile--budget${isActive ? ' active' : ''}${passLockClassFor('budget_note')}`
                  }
                  onClick={(e) => handleMenuClick(e, '/budget-note')}
                  aria-disabled={isFeatureLocked('budget_note') || undefined}
                >
                  <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="budget" /></span>
                  <span className="cp-sidebar-note-tile-label">예산 노트</span>
                </NavLink>
                <button
                  type="button"
                  className={`cp-sidebar-note-tile cp-sidebar-note-tile--curriculum${isCurriculumSubMenuActive ? ' active' : ''}`}
                  id="cp-sidebar-curriculum-trigger"
                  aria-label="교육과정 노트 — 해당 학기 연간진도표 열기"
                  onClick={() => {
                    void onCurriculumNoteClick();
                  }}
                >
                  <span className="cp-sidebar-note-tile-icon-wrap"><SidebarNoteTileIcon kind="curriculum" /></span>
                  <span className="cp-sidebar-note-tile-label">교육과정 노트</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
        <div
          className={`cp-menu-expandable cp-sidebar-curriculum-expandable${isCurriculumSubMenuActive ? ' is-active' : ''}`}
        >
          <div className="cp-sidebar-curriculum-vertical-stack">
            <div className="cp-sidebar-curriculum-section-head" aria-hidden="true">
              <SidebarNoteTileIcon kind="curriculum" />
              <span>교육과정 노트</span>
            </div>
            <div
              id="cp-sidebar-curriculum-sub"
              className="cp-menu-sublist cp-sidebar-curriculum-sublist"
              role="group"
              aria-labelledby="cp-sidebar-curriculum-trigger"
            >
          <div className="cp-sidebar-curriculum-class-band">
          <div
            ref={classPickerWrapRef}
            className="cp-sidebar-program-class-row cp-sidebar-program-class-picker-wrap"
            aria-live="polite"
            aria-label="프로그램 기준 클래스"
          >
          <button
            type="button"
            className={
              'cp-sidebar-program-class-picker-btn cp-grade-track-class-block cp-sidebar-program-class-pill' +
              (classPickerOpen ? ' is-open' : '')
            }
            aria-expanded={classPickerOpen}
            aria-haspopup="listbox"
            aria-label={
              focusClassParsed
                ? `프로그램 기준 클래스 ${focusClassParsed.title}, 목록 열기`
                : '프로그램 기준 클래스, 목록 열기'
            }
            onClick={() => setClassPickerOpen((o) => !o)}
          >
            <span className="cp-sidebar-program-class-picker-btn-main">
              {focusGradeRow ? (
                <SidebarClassPillInner grade={focusGradeRow} />
              ) : (
                <span className="cp-sidebar-program-class-picker-placeholder">클래스를 선택하세요</span>
              )}
            </span>
            <span
              className={`cp-sidebar-program-class-picker-chevron${classPickerOpen ? ' is-open' : ''}`}
              aria-hidden
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          {classPickerOpen ? (
            <ul className="cp-sidebar-program-class-dropdown" role="listbox" aria-label="등록된 클래스">
              {gradesSortedForPicker.length === 0 ? (
                <li className="cp-sidebar-program-class-dropdown-empty" role="presentation">
                  등록된 클래스가 없습니다.
                </li>
              ) : (
                gradesSortedForPicker.map((g) => {
                  const active = g.id === programFocusGradeId;
                  return (
                    <li key={g.id} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={
                          'cp-sidebar-program-class-dropdown-item cp-grade-track-class-block' +
                          (active ? ' is-active' : '')
                        }
                        onClick={() => applySidebarClassSelection(g.id)}
                      >
                        <SidebarClassPillInner grade={g} />
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          ) : null}
          </div>
        <div className="cp-sidebar-class-settings-stack">
        <div className="cp-menu-expandable cp-sidebar-preset-expandable cp-sidebar-class-individual-expandable">
          <button
            type="button"
            className={`cp-menu-expandable-trigger${classIndividualSectionActive ? ' cp-menu-expandable-trigger-active' : ''}`}
            aria-expanded={presetSettingsOpen}
            aria-controls="cp-sidebar-preset-sub"
            id="cp-sidebar-preset-trigger"
            onClick={() => {
              presetUserToggledRef.current = true;
              setPresetSettingsOpen((o) => !o);
            }}
          >
            <SidebarNoteTileIcon kind="settings" />
            <span className="cp-menu-expandable-label">클래스 사전 설정</span>
            <span
              className={`cp-menu-expandable-chevron${presetSettingsOpen ? ' is-open' : ''}`}
              aria-hidden="true"
            >
              ›
            </span>
          </button>
          {presetSettingsOpen ? (
            <div
              id="cp-sidebar-preset-sub"
              className="cp-menu-sublist cp-sidebar-preset-sublist"
              role="group"
              aria-labelledby="cp-sidebar-preset-trigger"
            >
              {classIndividualMenus.map((menu) => {
                const statusKey = PRESET_MENU_STATUS_KEY[menu.to as keyof typeof PRESET_MENU_STATUS_KEY];
                const showStatusDot = PRESET_MENU_STATUS_DOT_PATHS.has(menu.to);
                const isPresetReady = statusKey ? presetMenuGradeStatus[statusKey] : false;
                return (
                  <NavLink
                    key={menu.to}
                    to={menu.to}
                    className={({ isActive }) =>
                      `cp-menu-item cp-menu-sub-item cp-sidebar-preset-sublink${isActive ? ' active' : ''}`
                    }
                    onClick={(e) => handleMenuClick(e, menu.to)}
                  >
                    {showStatusDot ? (
                      <span
                        className={`cp-preset-status-dot cp-sidebar-preset-status-dot${isPresetReady ? ' is-ready' : ''}`}
                        aria-hidden="true"
                      />
                    ) : null}
                    {menu.labelLines ? (
                      <span className="cp-sidebar-preset-sublink-text">
                        <span className="cp-menu-line">{menu.labelLines[0]}</span>
                        <span className="cp-menu-line">{menu.labelLines[1]}</span>
                      </span>
                    ) : (
                      <span className="cp-sidebar-preset-sublink-text">{menu.label}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className="cp-menu-expandable cp-sidebar-preset-expandable cp-sidebar-class-common-expandable">
          <button
            type="button"
            className={`cp-menu-expandable-trigger${classCommonSectionActive ? ' cp-menu-expandable-trigger-active' : ''}`}
            aria-expanded={classCommonSettingsOpen}
            aria-controls="cp-sidebar-class-common-sub"
            id="cp-sidebar-class-common-trigger"
            onClick={() => setClassCommonSettingsOpen((o) => !o)}
          >
            <SidebarNoteTileIcon kind="plan" />
            <span className="cp-menu-expandable-label">학습내용, 평가계획</span>
            <span
              className={`cp-menu-expandable-chevron${classCommonSettingsOpen ? ' is-open' : ''}`}
              aria-hidden="true"
            >
              ›
            </span>
          </button>
          {classCommonSettingsOpen ? (
            <div
              id="cp-sidebar-class-common-sub"
              className="cp-menu-sublist cp-sidebar-preset-sublist"
              role="group"
              aria-labelledby="cp-sidebar-class-common-trigger"
            >
              {classCommonMenus.map((menu) => (
                <NavLink
                  key={menu.to}
                  to={menu.to}
                  className={({ isActive }) =>
                    `cp-menu-item cp-menu-sub-item cp-sidebar-preset-sublink${isActive ? ' active' : ''}${menuLockClass(menu.to)}`
                  }
                  onClick={(e) => handleMenuClick(e, menu.to)}
                  aria-disabled={menuLockClass(menu.to) !== '' || undefined}
                >
                  <span
                    className="cp-preset-status-dot cp-sidebar-preset-status-dot is-neutral"
                    aria-hidden="true"
                  />
                  <span className="cp-sidebar-preset-sublink-text">{menu.label}</span>
                </NavLink>
              ))}
            </div>
          ) : null}
        </div>
        </div>
        </div>
        <div className="cp-sidebar-curriculum-menu-panel">
        <div className="cp-sidebar-learning-links-stack" role="presentation">
          <NavLink
            to={recordMemosTo()}
            className={({ isActive }) =>
              `cp-menu-item cp-sidebar-record-memos-link${isActive ? ' active' : ''}${passLockClassFor('class_record_memos')}`
            }
            onClick={(e) => handleMenuClick(e, recordMemosTo())}
            aria-disabled={isFeatureLocked('class_record_memos') || undefined}
          >
            <span className="cp-sidebar-learning-link-icon-slot" aria-hidden="true">
              <img
                className="cp-sidebar-learning-link-icon cp-sidebar-learning-link-icon--page"
                src={`${process.env.PUBLIC_URL}/page.svg`}
                alt=""
                draggable={false}
              />
            </span>
            <span>기록 모아보기</span>
          </NavLink>
          <NavLink
            to={weeklyLearningGuideTo()}
            className={({ isActive }) =>
              `cp-menu-item cp-sidebar-weekly-learning-guide-link${isActive ? ' active' : ''}${passLockClassFor('weekly_learning_guide')}`
            }
            onClick={(e) => handleMenuClick(e, weeklyLearningGuideTo())}
            aria-disabled={isFeatureLocked('weekly_learning_guide') || undefined}
          >
            <span className="cp-sidebar-learning-link-icon-slot" aria-hidden="true">
              <img
                className="cp-sidebar-learning-link-icon cp-sidebar-learning-link-icon--weektable"
                src={`${process.env.PUBLIC_URL}/weektable.svg`}
                alt=""
                draggable={false}
              />
            </span>
            <span>주간학습 안내</span>
          </NavLink>
        </div>

        <div
          className="cp-sidebar-divider cp-sidebar-divider-after-primary-nav"
          role="separator"
          aria-hidden="true"
        />
        <div className="cp-sidebar-marked-nav-row">
          <NavLink
            to="/annual-timetable"
            className={({ isActive }) =>
              `cp-menu-item cp-sidebar-primary-link cp-sidebar-primary-annual-timetable${isActive ? ' active' : ''}`
            }
            onClick={(e) => handleMenuClick(e, '/annual-timetable')}
          >
            <span className="cp-sidebar-learning-link-icon-slot" aria-hidden="true">
              <img
                className="cp-sidebar-learning-link-icon cp-sidebar-learning-link-icon--table"
                src={`${process.env.PUBLIC_URL}/table.svg`}
                alt=""
                draggable={false}
              />
            </span>
            <span>연간시간표</span>
          </NavLink>
        </div>

        <div className="cp-sidebar-program-block" aria-label="프로그램 메뉴">
          <div className="cp-sidebar-annual-progress-row" role="group" aria-label="연간진도표 바로가기">
            {orderedAcademicTerms(swapTermOrder).map((term) => {
              const termPath: '/annual-progress-term1' | '/annual-progress-term2' =
                term === 1 ? '/annual-progress-term1' : '/annual-progress-term2';
              const linkTermClass =
                term === 1 ? 'cp-sidebar-annual-progress-link-t1' : 'cp-sidebar-annual-progress-link-t2';
              return (
                <div key={termPath} className="cp-sidebar-marked-nav-row">
                  <NavLink
                    to={annualProgressTo(termPath)}
                    className={({ isActive }) =>
                      `cp-menu-item cp-sidebar-annual-progress-link ${linkTermClass}${isActive ? ' active' : ''}`
                    }
                    onClick={(e) => onAnnualProgressTermNavClick(e, termPath)}
                  >
                    <span className="cp-sidebar-learning-link-icon-slot" aria-hidden="true">
                      <img
                        className="cp-sidebar-learning-link-icon cp-sidebar-learning-link-icon--year-table"
                        src={`${process.env.PUBLIC_URL}/Year_Table.svg`}
                        alt=""
                        draggable={false}
                      />
                    </span>
                    <span>{term}학기 연간진도표</span>
                  </NavLink>
                </div>
              );
            })}
          </div>

          <div
            className="cp-sidebar-divider cp-sidebar-divider-after-class-block"
            role="separator"
            aria-hidden="true"
          />

          <div className="cp-menu-expandable cp-sidebar-program-type-expandable">
            <div className="cp-sidebar-marked-nav-row">
              <span
                className="cp-class-record-memos-program-mark cp-class-record-memos-program-mark--fixed"
                aria-hidden="true"
              />
              <button
                type="button"
                className={`cp-menu-expandable-trigger${fixedProgramSectionActive ? ' cp-menu-expandable-trigger-active' : ''}`}
                aria-expanded={fixedProgramOpen}
                aria-controls="cp-sidebar-fixed-program-sub"
                id="cp-sidebar-fixed-program-trigger"
                onClick={() => setFixedProgramOpen((o) => !o)}
              >
                <span className="cp-menu-expandable-label">고정 프로그램</span>
                <span
                  className={`cp-menu-expandable-chevron${fixedProgramOpen ? ' is-open' : ''}`}
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>
            </div>
            {fixedProgramOpen ? (
              <div
                id="cp-sidebar-fixed-program-sub"
                className="cp-menu-sublist"
                role="group"
                aria-labelledby="cp-sidebar-fixed-program-trigger"
              >
                {!programFocusGradeId ? (
                  <button
                    type="button"
                    className="cp-menu-sub-empty cp-menu-sub-empty-action"
                    onClick={() => openClassRequiredModal(null)}
                  >
                    클래스를 먼저 선택해 주세요.
                  </button>
                ) : (
                  <>
                    {orderedAcademicTerms(swapTermOrder).map((term) => (
                      <NavLink
                        key={term}
                        to={fixedProgramTermTo(term)}
                        className={() =>
                          `cp-menu-item cp-menu-sub-item cp-menu-sub-item-term-row${fixedTermIsActive(term) ? ' active' : ''}`
                        }
                        onClick={(e) => handleMenuClick(e, fixedProgramTermTo(term))}
                        aria-label={`${term}학기 고정 프로그램`}
                      >
                        <span
                          className={`cp-sidebar-term-chip cp-sidebar-term-chip-t${term}`}
                          aria-hidden="true"
                        >
                          {term}
                        </span>
                        <span className="cp-menu-sub-item-text">{term}학기</span>
                      </NavLink>
                    ))}
                  </>
                )}
              </div>
            ) : null}
          </div>

          <div className="cp-menu-expandable cp-sidebar-program-type-expandable">
            <div className="cp-sidebar-marked-nav-row">
              <span
                className="cp-class-record-memos-program-mark cp-class-record-memos-program-mark--project"
                aria-hidden="true"
              />
              <button
                type="button"
                className={`cp-menu-expandable-trigger${projectSectionActive ? ' cp-menu-expandable-trigger-active' : ''}`}
                aria-expanded={projectOpen}
                aria-controls="cp-sidebar-project-sub"
                id="cp-sidebar-project-trigger"
                onClick={() => setProjectOpen((o) => !o)}
              >
                <span className="cp-menu-expandable-label">프로젝트 프로그램</span>
                <span
                  className={`cp-menu-expandable-chevron${projectOpen ? ' is-open' : ''}`}
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>
            </div>
            {projectOpen ? (
              <div
                id="cp-sidebar-project-sub"
                className="cp-menu-sublist"
                role="group"
                aria-labelledby="cp-sidebar-project-trigger"
              >
                {!programFocusGradeId ? (
                  <button
                    type="button"
                    className="cp-menu-sub-empty cp-menu-sub-empty-action"
                    onClick={() => openClassRequiredModal(null)}
                  >
                    클래스를 먼저 선택해 주세요.
                  </button>
                ) : projectMenuItems.length === 0 ? (
                  <span className="cp-menu-sub-empty">이 클래스에 등록된 프로젝트가 없습니다.</span>
                ) : (
                  projectMenuItems.map((item) => {
                    const to = projectDetailTo(item.id, item.termNumber);
                    return (
                      <NavLink
                        key={`${item.id}-${item.termNumber}`}
                        to={to}
                        className={() =>
                          `cp-menu-item cp-menu-sub-item cp-menu-sub-item-term-row${projectLinkIsActive(item.id, item.termNumber) ? ' active' : ''}`
                        }
                        onClick={(e) => handleMenuClick(e, to)}
                        title={`${item.name} · ${item.termNumber}학기`}
                      >
                        <span
                          className={`cp-sidebar-term-chip cp-sidebar-term-chip-t${item.termNumber}`}
                          aria-hidden="true"
                        >
                          {item.termNumber}
                        </span>
                        <span className="cp-menu-sub-item-text">{item.name}</span>
                      </NavLink>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>

          <div className="cp-menu-expandable cp-sidebar-program-type-expandable">
            <div className="cp-sidebar-marked-nav-row">
              <span
                className="cp-class-record-memos-program-mark cp-class-record-memos-program-mark--track"
                aria-hidden="true"
              />
              <button
                type="button"
                className={`cp-menu-expandable-trigger${trackSectionActive ? ' cp-menu-expandable-trigger-active' : ''}`}
                aria-expanded={trackProgramsOpen}
                aria-controls="cp-sidebar-track-sub"
                id="cp-sidebar-track-trigger"
                onClick={() => setTrackProgramsOpen((o) => !o)}
              >
                <span className="cp-menu-expandable-label">편제별 프로그램</span>
                <span
                  className={`cp-menu-expandable-chevron${trackProgramsOpen ? ' is-open' : ''}`}
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>
            </div>
            {trackProgramsOpen ? (
              <div
                id="cp-sidebar-track-sub"
                className="cp-menu-sublist"
                role="group"
                aria-labelledby="cp-sidebar-track-trigger"
              >
                {!programFocusGradeId ? (
                  <button
                    type="button"
                    className="cp-menu-sub-empty cp-menu-sub-empty-action"
                    onClick={() => openClassRequiredModal(null)}
                  >
                    클래스를 먼저 선택해 주세요.
                  </button>
                ) : trackMenuRows.length === 0 ? (
                  <span className="cp-menu-sub-empty">
                    이 클래스에 등록된 편제가 없습니다. 편제 및 기준 시수에서 설정해 주세요.
                  </span>
                ) : (
                  trackMenuRows.map(({ name, term, timetableName, label, isSyncRep }) => {
                    const to = trackLinkTo(name, term, timetableName);
                    return (
                      <NavLink
                        key={`${name}-${timetableName ?? 'all'}-t${term}`}
                        to={to}
                        className={() =>
                          `cp-menu-item cp-menu-sub-item cp-menu-sub-item-term-row${trackLinkIsActive(name, term, timetableName) ? ' active' : ''}`
                        }
                        onClick={(e) => handleMenuClick(e, to)}
                        title={`${label} · ${term}학기`}
                      >
                        <span
                          className={`cp-sidebar-term-chip cp-sidebar-term-chip-t${term}`}
                          aria-hidden="true"
                        >
                          {term}
                        </span>
                        <span className="cp-menu-sub-item-text cp-menu-sub-item-track-label">
                          <span>{label}</span>
                          {isSyncRep ? (
                            <span className="cp-sidebar-track-rep-badge" aria-label="대표 프로그램">
                              대표
                            </span>
                          ) : null}
                        </span>
                      </NavLink>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        </div>
        </div>
              </div>
          </div>
        </div>
      </nav>
      {classRequiredModal
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-sidebar-class-required-title"
              onClick={closeClassRequiredModal}
            >
              <div
                className="cp-modal cp-sidebar-class-required-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="cp-sidebar-class-required-title">클래스 선택</h3>
                <p className="page-desc cp-sidebar-class-required-desc">
                  {gradesSortedForPicker.length === 0
                    ? '등록된 클래스가 없습니다. 클래스 설정에서 클래스를 먼저 등록해 주세요.'
                    : '페이지를 열기 위해서는 하나의 클래스를 선택해주세요'}
                </p>
                <div className="cp-modal-body">
                  <div
                    ref={classRequiredModalPickerRef}
                    className="cp-sidebar-class-required-picker-wrap"
                  >
                    <button
                      type="button"
                      className={
                        'cp-sidebar-program-class-picker-btn cp-grade-track-class-block cp-sidebar-program-class-pill cp-sidebar-class-required-picker-btn' +
                        (classRequiredModalPickerOpen ? ' is-open' : '')
                      }
                      aria-expanded={classRequiredModalPickerOpen}
                      aria-haspopup="listbox"
                      aria-label="클래스 선택"
                      onClick={() => setClassRequiredModalPickerOpen((o) => !o)}
                    >
                      <span className="cp-sidebar-program-class-picker-btn-main">
                        <span className="cp-sidebar-program-class-picker-placeholder">
                          클래스를 선택하세요
                        </span>
                      </span>
                      <span
                        className={`cp-sidebar-program-class-picker-chevron${classRequiredModalPickerOpen ? ' is-open' : ''}`}
                        aria-hidden
                      >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path
                            d="M6 9l6 6 6-6"
                            stroke="currentColor"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </button>
                    {classRequiredModalPickerOpen ? (
                      <ul
                        className="cp-sidebar-program-class-dropdown cp-sidebar-class-required-dropdown"
                        role="listbox"
                        aria-label="등록된 클래스"
                      >
                        {gradesSortedForPicker.length === 0 ? (
                          <li className="cp-sidebar-program-class-dropdown-empty" role="presentation">
                            등록된 클래스가 없습니다.
                          </li>
                        ) : (
                          gradesSortedForPicker.map((g) => (
                            <li key={g.id} role="presentation">
                              <button
                                type="button"
                                role="option"
                                aria-selected={false}
                                className="cp-sidebar-program-class-dropdown-item cp-grade-track-class-block"
                                onClick={() => confirmClassRequiredAndOpen(g.id)}
                              >
                                <SidebarClassPillInner grade={g} />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    ) : null}
                  </div>
                </div>
                <div className="cp-modal-footer">
                  <button type="button" className="cp-btn-secondary" onClick={closeClassRequiredModal}>
                    닫기
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </aside>
  );
};

export default SideMenu;
